import { useContext } from "react";
import { DropdownOption, getById, getByIds, mapToDropdownOption, ValidationResult } from "sprint-common-web";

import { AirlinesDataContext } from "../../../../common/data/lookup-data-providers/AirlinesDataProvider";
import {
    Distributor,
    DistributorsDataContext
} from "../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import {
    VersionType,
    VersionTypesDataContext
} from "../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { Airline } from "../../../../common/types/Airline";
import { UpdateAncillaryRequest } from "../../api/UpdateAncillaryRequest";
import { useAssetsManageAncillaryApi } from "../../api/useAssetsManageAncillaryApi";
import { ManageAncillaryContext } from "../../context/ManageAncillaryContext";
import { AncillaryItem } from "../AncillaryItem";
import { UpdatedAncillaryItem } from "./EditAncillaryForm";
import { EditAncillaryFormInputs } from "./EditAncillaryFormInputs";

export const useEditAncillaryForm = (ancillaryToEditId: string) => {
    const { airlines } = useContext(AirlinesDataContext);
    const { nonTrailerVersionTypes: versionTypes } = useContext(VersionTypesDataContext);
    const { getDistributorById } = useContext(DistributorsDataContext);

    const { validateUpdateAncillary, updateAncillary } = useAssetsManageAncillaryApi();

    const { ancillaryItems } = useContext(ManageAncillaryContext);

    const { defaultValues, defaultOptions } = getDefaultValues(ancillaryItems, ancillaryToEditId);

    const validateEditAncillary = async (inputs: EditAncillaryFormInputs): Promise<ValidationResult> => {
        return validateUpdateAncillary(ancillaryToEditId, { versionTypeId: inputs.versionTypeId });
    };

    const editAncillary = async (inputs: EditAncillaryFormInputs) => {
        const { title, airlineIds, versionTypeId, distributorId } = inputs;
        const ancillaryToEdit = getById(ancillaryItems, ancillaryToEditId);

        const versionType = getById(versionTypes, versionTypeId);
        const airlineList = getByIds(airlines, airlineIds);

        let distributor = ancillaryToEdit?.distributor;
        if (distributorId !== ancillaryToEdit?.distributor.id) {
            distributor = await getDistributorById(distributorId);
        }

        return updateAncillary(
            ancillaryToEditId,
            createUpdateRequest({ title, airlineIds, versionTypeId, distributorId })
        ).then(() =>
            getUpdatedAncillary({ ancillaryToEditId, title, airlines: airlineList, versionType, distributor })
        );
    };

    return {
        defaultValues,
        defaultOptions,
        airlines,
        versionTypes,
        validateEditAncillary,
        editAncillary
    };
};

const getUpdatedAncillary = (ancillaryParams: {
    ancillaryToEditId: string;
    title: string;
    airlines: Airline[];
    versionType: VersionType;
    distributor: Distributor;
}): UpdatedAncillaryItem => {
    return {
        id: ancillaryParams.ancillaryToEditId,
        data: {
            name: ancillaryParams.title,
            airlines: ancillaryParams.airlines,
            versionType: ancillaryParams.versionType,
            distributor: ancillaryParams.distributor
        }
    };
};

const createUpdateRequest = (requestParams: {
    title: string;
    airlineIds: string[];
    versionTypeId: string;
    distributorId: string | undefined;
}): UpdateAncillaryRequest => {
    return {
        title: requestParams.title,
        airlineIds: requestParams.airlineIds,
        versionTypeId: requestParams.versionTypeId,
        distributorId: requestParams.distributorId
    };
};

const getDefaultValues = (ancillaries: AncillaryItem[], ancillaryToEditId: string): EditAncillaryFormDefaults => {
    const ancillaryToEdit = getById(ancillaries, ancillaryToEditId);

    return {
        defaultValues: {
            title: ancillaryToEdit.title,
            airlineIds: ancillaryToEdit.airlines.map((item) => item.id),
            versionTypeId: ancillaryToEdit.versionType.id,
            distributorId: ancillaryToEdit.distributor.id
        },
        defaultOptions: {
            distributors: ancillaryToEdit.distributor ? [mapToDropdownOption(ancillaryToEdit.distributor)] : []
        }
    };
};

interface EditAncillaryFormDefaults {
    defaultValues: EditAncillaryFormInputs;
    defaultOptions: {
        distributors: DropdownOption[];
    };
}
